import { WHITE_ROOM_ENVIRONMENT } from "../utils/constants.js";
import { readCustomFloor } from "../core/UserPreferences.js";

/** The environment picker grid and the per-environment control visibility that follows it. */
export class EnvironmentPanel {
  constructor(app, uiManager) {
    this.app = app;
    this.ui = uiManager;
  }

  markSelectedEnvironment(environmentKey) {
    for (const item of document.querySelectorAll("#environments-grid .environment-item")) {
      item.classList.toggle("selected", item.dataset.environmentId === environmentKey);
    }
  }

  async populateEnvironmentPanel() {
    const sceneManager = this.app.getSceneManager();
    if (!sceneManager) return;

    this.populateEnvironments();
  }

  populateEnvironments() {
    const sceneManager = this.app.getSceneManager();
    const envManager = sceneManager.getEnvironmentManager();
    const environments = sceneManager.getEnvironments();
    const currentEnvironment = sceneManager.getCurrentEnvironment();

    const environmentGrid = document.getElementById("environments-grid");
    if (!environmentGrid) return;

    environmentGrid.innerHTML = "";

    Object.entries(environments).forEach(([key, environment]) => {
      const item = document.createElement("div");
      item.className = `environment-item ${key === currentEnvironment ? "selected" : ""}`;
      item.dataset.environmentId = key;

      // Create thumbnail container
      const thumbnail = document.createElement("div");
      thumbnail.className = "environment-thumbnail";

      // Generate composite thumbnail showing floor and wall
      this._generateEnvironmentThumbnail(thumbnail, environment, envManager);

      const label = document.createElement("div");
      label.className = "environment-label";
      // Use localized name if nameKey exists, otherwise fall back to name
      label.textContent = environment.nameKey
        ? this.ui.t(environment.nameKey, environment.name)
        : environment.name;

      // Add description tooltip
      if (environment.description) {
        item.title = environment.description;
      }

      item.appendChild(thumbnail);
      item.appendChild(label);

      item.addEventListener("click", async () => {
        // Room materials rebuild synchronously, so the new environment's textures have to
        // be on disk first — this click is the one async seam above that chain.
        await this.app.getSceneManager()?.ensureEnvironmentAssets(key);
        this.changeEnvironment(key);
      });

      environmentGrid.appendChild(item);
    });
  }

  /**
   * Generate a composite thumbnail showing floor and wall textures
   * @param {HTMLElement} thumbnailEl - The thumbnail element to populate
   * @param {Object} environment - Environment configuration
   * @param {Object} envManager - Environment manager instance
   */
  _generateEnvironmentThumbnail(thumbnailEl, environment, envManager) {
    const floorMaterials = envManager.getFloorMaterials();
    const wallMaterials = envManager.getWallMaterials();
    const floorSettings = envManager.floorSettings;
    const wallSettings = envManager.wallSettings;

    // Special case: white room with no walls
    if (environment.backgroundColor && !environment.wall) {
      thumbnailEl.style.background = `linear-gradient(to bottom, ${environment.backgroundColor} 50%, #fcfcfc 50%)`;
      return;
    }

    // Get floor texture path
    let floorImagePath = null;
    const floorMaterial = environment.floor ? floorMaterials[environment.floor] : null;
    if (floorMaterial && floorMaterial.textures?.diffuse && floorMaterial.textureFolder) {
      floorImagePath = `${floorSettings.textureBasePath}${floorMaterial.textureFolder}/${floorMaterial.textures.diffuse}`;
    }

    // Get wall texture path
    let wallImagePath = null;
    const wallMaterial = environment.wall ? wallMaterials[environment.wall] : null;
    if (wallMaterial && wallMaterial.textures?.diffuse && wallMaterial.textureFolder) {
      wallImagePath = `${wallSettings.textureBasePath}${wallMaterial.textureFolder}/${wallMaterial.textures.diffuse}`;
    }

    // Create canvas for composite thumbnail
    const canvas = document.createElement('canvas');
    canvas.width = 120;
    canvas.height = 80;
    const ctx = canvas.getContext('2d');

    // Track loaded images
    let loadedCount = 0;
    const totalToLoad = (floorImagePath ? 1 : 0) + (wallImagePath ? 1 : 0);

    // If no textures to load, use fallback colors
    if (totalToLoad === 0) {
      this._drawFallbackThumbnail(ctx, canvas, floorMaterial, wallMaterial);
      thumbnailEl.style.backgroundImage = `url(${canvas.toDataURL()})`;
      return;
    }

    const floorImg = floorImagePath ? new Image() : null;
    const wallImg = wallImagePath ? new Image() : null;

    const onAllLoaded = () => {
      loadedCount++;
      if (loadedCount < totalToLoad) return;

      // Draw composite: wall on top (60%), floor on bottom (40%) with perspective
      const wallHeight = canvas.height * 0.55;
      const floorHeight = canvas.height * 0.45;

      // Draw wall (top portion)
      if (wallImg) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, canvas.width, wallHeight);
        ctx.clip();
        // Scale to cover width, maintain aspect
        const wallScale = Math.max(canvas.width / wallImg.width, wallHeight / wallImg.height);
        const wallW = wallImg.width * wallScale;
        const wallH = wallImg.height * wallScale;
        ctx.drawImage(wallImg, (canvas.width - wallW) / 2, (wallHeight - wallH) / 2, wallW, wallH);
        ctx.restore();
      } else {
        ctx.fillStyle = '#e8e8e8';
        ctx.fillRect(0, 0, canvas.width, wallHeight);
      }

      // Draw floor (bottom portion) with slight perspective transform
      if (floorImg) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, wallHeight, canvas.width, floorHeight);
        ctx.clip();
        // Scale to cover
        const floorScale = Math.max(canvas.width / floorImg.width, floorHeight / floorImg.height) * 1.2;
        const floorW = floorImg.width * floorScale;
        const floorH = floorImg.height * floorScale;
        ctx.drawImage(floorImg, (canvas.width - floorW) / 2, wallHeight, floorW, floorH);
        ctx.restore();
      } else {
        ctx.fillStyle = '#c4a77d';
        ctx.fillRect(0, wallHeight, canvas.width, floorHeight);
      }

      // Add subtle shadow line at wall-floor junction
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, wallHeight);
      ctx.lineTo(canvas.width, wallHeight);
      ctx.stroke();

      // Add subtle vignette for depth
      const gradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 2, 0,
        canvas.width / 2, canvas.height / 2, canvas.width * 0.8
      );
      gradient.addColorStop(0, 'rgba(0,0,0,0)');
      gradient.addColorStop(1, 'rgba(0,0,0,0.15)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      thumbnailEl.style.backgroundImage = `url(${canvas.toDataURL()})`;
    };

    const onError = () => {
      loadedCount++;
      if (loadedCount >= totalToLoad) {
        // Fallback on error
        this._drawFallbackThumbnail(ctx, canvas, floorMaterial, wallMaterial);
        thumbnailEl.style.backgroundImage = `url(${canvas.toDataURL()})`;
      }
    };

    if (floorImg) {
      floorImg.onload = onAllLoaded;
      floorImg.onerror = onError;
      floorImg.src = floorImagePath;
    }

    if (wallImg) {
      wallImg.onload = onAllLoaded;
      wallImg.onerror = onError;
      wallImg.src = wallImagePath;
    }
  }

  /**
   * Draw a fallback thumbnail using colors when textures aren't available
   */
  _drawFallbackThumbnail(ctx, canvas, floorMaterial, wallMaterial) {
    const wallHeight = canvas.height * 0.55;

    // Wall color
    ctx.fillStyle = '#e8e8e8';
    ctx.fillRect(0, 0, canvas.width, wallHeight);

    // Floor color
    ctx.fillStyle = '#c4a77d';
    ctx.fillRect(0, wallHeight, canvas.width, canvas.height - wallHeight);

    // Shadow line
    ctx.strokeStyle = 'rgba(0,0,0,0.2)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, wallHeight);
    ctx.lineTo(canvas.width, wallHeight);
    ctx.stroke();
  }

  changeEnvironment(environmentKey) {
    const sceneManager = this.app.getSceneManager();
    if (sceneManager.changeEnvironment(environmentKey)) {
      // Disable white floor override so the new environment's floor shows correctly
      sceneManager.getRoomBuilder()?.setWhiteFloor(false);
      this.ui._syncWhiteFloorToggle();

      this.markSelectedEnvironment(environmentKey);

      // Notify camera manager to update restrictions based on new environment
      this.app.getCameraManager()?.onEnvironmentChange();

      // Show room walls button only in white_room
      this.updateRoomBoxBtnVisibility(environmentKey);

      // Restore custom floor texture when switching to white_room
      if (environmentKey === WHITE_ROOM_ENVIRONMENT) {
        const floor = readCustomFloor();
        if (floor?.dataURL) this.app.getSceneManager()?.getRoomBuilder()?.applyCustomFloorTexture(floor.dataURL);
      }
    }
  }

  updateRoomBoxBtnVisibility(environmentKey) {
    const roomBoxItem = document.getElementById("room-box-item");
    if (!roomBoxItem) return;
    if (environmentKey === WHITE_ROOM_ENVIRONMENT) {
      roomBoxItem.classList.remove("hidden");
    } else {
      roomBoxItem.classList.add("hidden");
      this.ui._collapseHamburgerSection(roomBoxItem);
      // Disable room walls if they were enabled
      const roomBox = this.app.getSceneManager()?.getRoomBox();
      if (roomBox?.enabled) {
        roomBox.hide();
        const toggle = document.getElementById("room-box-toggle");
        if (toggle) toggle.checked = false;
        document.getElementById("room-box-size-controls")?.classList.add("hidden");
        this.app.getStateManager()?.saveSceneState();
      }
    }

    // Floor upload button
    const floorUploadItem = document.getElementById("floor-upload-item");
    if (floorUploadItem) {
      if (environmentKey === WHITE_ROOM_ENVIRONMENT) {
        floorUploadItem.classList.remove("hidden");
      } else {
        floorUploadItem.classList.add("hidden");
        this.ui._collapseHamburgerSection(floorUploadItem);
        // Clear custom floor when leaving white_room
        const roomBuilder = this.app.getSceneManager()?.getRoomBuilder();
        roomBuilder?.clearCustomFloorTexture();
      }
    }
  }
}
